import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Mtds, Sample, useTheme } from 'prove-common'
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Feature, { FeatureLike } from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Extent } from 'ol/extent'
import { MapBrowserEvent, Overlay } from 'ol'
import { MapViewSampleSidebar } from '../map-view-sample-sidebar/MapViewSampleSidebar'
import {
  createFeaturesFromSamples,
  getBaseLayerSource,
  getClusterExtent,
  getClusterStyle,
  toMapCoords
} from '../../utils/mapUtils'
import BaseEvent from 'ol/events/Event'
import { Cluster } from 'ol/source'
import { Coordinate } from 'ol/coordinate'
import { MapTooltip } from '../MapTooltip'

type MapSamplesViewProps = Readonly<{
  samples: Sample[]
  loadingProgress?: { value: number; max: number }
  isLoading: boolean
  topRightControl?: React.ReactNode
}>

export function MapSamplesView({
  samples,
  loadingProgress,
  isLoading,
  topRightControl
}: MapSamplesViewProps) {
  // --- REFS & STATE ---
  const mapElementRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Map | null>(null)
  const vectorSourceRef = useRef<VectorSource<Feature<Point>> | null>(null)
  const clusterSourceRef = useRef<Cluster | null>(null)
  const tileLayerRef = useRef<TileLayer>(null)
  const pendingZoomExtentRef = useRef<Extent | null>(null)
  const tooltipContainerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<Overlay | null>(null)

  const [mapCursor, setMapCursor] = useState('')
  const [detailPage, setDetailPage] = useState('')
  const [tooltipData, setTooltipData] = useState<Sample[]>([])
  const [clickedPosition, setClickedPosition] = useState<
    Coordinate | undefined
  >(undefined)

  const { isDarkMode } = useTheme()
  const isDarkModeRef = useRef(isDarkMode)
  const samplesRef = useRef(samples)

  // --- SYNC EFFECTS ---
  useEffect(() => {
    isDarkModeRef.current = isDarkMode
  }, [isDarkMode])
  useEffect(() => {
    samplesRef.current = samples
  }, [samples])

  // --- 1. INITIALIZATION EFFECT (Runs ONCE) ---
  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return

    // Setup Sources
    const vectorSource = new VectorSource<Feature<Point>>()
    vectorSourceRef.current = vectorSource

    const clusterSource = new Cluster({
      distance: 45,
      minDistance: 30,
      source: vectorSource
    })
    clusterSourceRef.current = clusterSource

    // Setup Layers
    const vectorLayer = new VectorLayer({
      source: clusterSource,
      style: (f) => getClusterStyle(f, isDarkModeRef.current)
    })

    const tileLayer = new TileLayer({
      source: getBaseLayerSource(isDarkModeRef.current)
    })
    tileLayerRef.current = tileLayer

    // Create Map
    const map = new Map({
      target: mapElementRef.current,
      layers: [tileLayer, vectorLayer],
      view: new View({
        center: toMapCoords([12.307778, 63.990556]),
        zoom: 5
      })
    })
    mapRef.current = map

    // D. Setup Tooltip Overlay
    const overlay = new Overlay({
      element: tooltipContainerRef.current!,
      stopEvent: true,
      positioning: 'center-center',
      offset: [0, 0]
    })
    map.addOverlay(overlay)
    overlayRef.current = overlay

    // Click Handler
    const clickHandler = (evt: BaseEvent) => {
      const mapEvent = evt as MapBrowserEvent<PointerEvent>
      const view = map.getView()

      const target = mapEvent.originalEvent.target as HTMLElement
      if (tooltipContainerRef.current?.contains(target)) return

      const feature = map.forEachFeatureAtPixel(mapEvent.pixel, (f) => f, {
        hitTolerance: 0
      })

      if (!feature) {
        setClickedPosition(undefined) // Hide tooltip
        setTooltipData([])
        setDetailPage('')
        return
      }

      const features = feature.get('features')
      const geometry = feature.getGeometry() as Point
      const clusterIconCoord = geometry.getCoordinates()

      // --- CASE A: CLUSTER ---
      if (features.length > 1) {
        const foundIds = new Set<string>()
        const samplesInCluster = features
          .map((f: FeatureLike) => {
            const sId = f.get('sampleId')
            if (foundIds.has(sId)) return null
            foundIds.add(sId)
            return samplesRef.current.find((s) => s.id === sId)
          })
          .filter(Boolean) as Sample[]

        // Update Data AND Position State
        setTooltipData(samplesInCluster)
        setClickedPosition(clusterIconCoord)

        // --- ZOOM CALCULATION ---
        const clusterExtent = getClusterExtent(feature)
        const currentZoom = view.getZoom() || 0
        let targetZoom = currentZoom

        const isSameLocation =
          clusterExtent &&
          clusterExtent[0] === clusterExtent[2] &&
          clusterExtent[1] === clusterExtent[3]

        if (isSameLocation) {
          targetZoom = Math.min(currentZoom + 2, 15)
        } else if (clusterExtent) {
          // Calculate the physical size of the cluster on the map
          const width = clusterExtent[2] - clusterExtent[0]
          const height = clusterExtent[3] - clusterExtent[1]
          const maxDist = Math.max(width, height)

          const CLUSTER_DISTANCE_PX = 45

          // Calculate the Resolution where: (maxDist / Resolution) == 45
          const minSafeResolution = maxDist / CLUSTER_DISTANCE_PX

          // Convert that resolution to a Zoom Level
          // This is the "Breaking Point" zoom.
          const maxSafeZoom = view.getZoomForResolution(minSafeResolution) || 20

          // Determine Target
          targetZoom = Math.min(maxSafeZoom - 0.5, 15)
        }

        targetZoom = Math.max(currentZoom, targetZoom)

        // Offset logic (move map North)
        const targetResolution = view.getResolutionForZoom(targetZoom) || 1
        const shiftInMeters = 200 * targetResolution

        view.animate({
          center: [clusterIconCoord[0], clusterIconCoord[1] + shiftInMeters],
          zoom: targetZoom,
          duration: 500
        })
      }
      // --- CASE B: SINGLE POINT ---
      else if (features.length === 1) {
        setDetailPage(features[0].get('sampleId'))
        view.animate({ center: clusterIconCoord, duration: 300 })
      }
    }

    const pointerMoveHandler = (evt: BaseEvent) => {
      const mapEvent = evt as MapBrowserEvent<PointerEvent>
      const hit = map.hasFeatureAtPixel(mapEvent.pixel)
      setMapCursor(hit ? 'pointer' : '')
    }

    map.on('click', clickHandler)
    map.on('pointermove', pointerMoveHandler)

    // RESIZE OBSERVER
    const resizeObserver = new ResizeObserver(() => {
      map.updateSize()
    })
    resizeObserver.observe(mapElementRef.current)

    return () => {
      resizeObserver.disconnect() // Cleanup
      map.setTarget()
      mapRef.current = null
      overlayRef.current = null
    }
  }, [])

  // --- 2. POSITIONING EFFECT ---
  useEffect(() => {
    if (overlayRef.current) {
      if (clickedPosition && tooltipData.length > 0) {
        overlayRef.current.setPosition(clickedPosition)
      } else {
        overlayRef.current.setPosition(undefined)
      }
    }
  }, [clickedPosition, tooltipData])

  // --- 3. HELPER LOGIC ---
  const triggerSafeZoom = useCallback((targetExtent: Extent) => {
    const map = mapRef.current
    if (!map) return
    const view = map.getView()
    if (view.getAnimating()) {
      pendingZoomExtentRef.current = targetExtent
      return
    }
    const performZoom = (extent: Extent) => {
      if (extent[0] === Infinity) return
      view.fit(extent, {
        padding: [50, 50, 50, 50],
        duration: 500,
        maxZoom: 14,
        callback: (finished) => {
          if (finished && pendingZoomExtentRef.current) {
            const nextExtent = pendingZoomExtentRef.current
            pendingZoomExtentRef.current = null
            performZoom(nextExtent)
          }
        }
      })
    }
    performZoom(targetExtent)
  }, [])

  // Memoize the transformation
  const features = React.useMemo(() => {
    return createFeaturesFromSamples(samples)
  }, [samples])

  // Use the memoized features in the effect
  useEffect(() => {
    const source = vectorSourceRef.current
    if (!source) return

    source.clear()
    if (features.length > 0) {
      source.addFeatures(features) // Much faster if this was just a re-render
      const totalExtent = source.getExtent()
      if (totalExtent) {
        triggerSafeZoom(totalExtent)
      }
    }
    // ...
  }, [features, triggerSafeZoom])

  useEffect(() => {
    if (tileLayerRef.current && mapRef.current) {
      tileLayerRef.current.setSource(getBaseLayerSource(isDarkMode))
      mapRef.current
        .getLayers()
        .forEach((l) => l instanceof VectorLayer && l.changed())
    }
  }, [isDarkMode])

  return (
    <Mtds.Flex>
      <div data-self='500' style={{ position: 'relative' }}>
        <Mtds.Card
          className='overflow-hidden'
          data-pad='0'
          id='map'
          ref={mapElementRef}
          tabIndex={0}
          onMouseEnter={() => {
            mapElementRef.current?.focus({ preventScroll: true })
          }}
          style={{
            width: '100%',
            height: '100%',
            cursor: mapCursor,
            outline: 'none'
          }}
        />
        <MapTooltip
          containerRef={tooltipContainerRef}
          data={tooltipData}
          onClose={() => {
            setClickedPosition(undefined)
            setTooltipData([])
          }}
          onItemClick={(id) => setDetailPage(id)}
        />

        {topRightControl && (
          <div
            role='region'
            aria-label='Kartinnstillinger'
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 100,
              pointerEvents: 'auto'
            }}
          >
            <Mtds.Card data-pad='3'>{topRightControl}</Mtds.Card>
          </div>
        )}

        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: isDarkMode
                ? 'rgba(0, 0, 0, 0.6)'
                : 'rgba(255, 255, 255, 0.6)',
              color: isDarkMode ? 'white' : 'black',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
          >
            {loadingProgress ? (
              <Mtds.Grid>
                <p className={'w-64 text-center'}>
                  {`Laster… (${Math.round((loadingProgress.value / loadingProgress.max) * 100)}%)`}
                </p>
                <Mtds.Progress
                  value={loadingProgress.value}
                  max={loadingProgress.max}
                />
              </Mtds.Grid>
            ) : (
              <p>Laster...</p>
            )}
          </div>
        )}
      </div>

      {detailPage && (
        <div data-self='200'>
          <MapViewSampleSidebar sampleId={detailPage} />
        </div>
      )}
    </Mtds.Flex>
  )
}
